import { routes, type TelegramAuthResponse } from '@spot/shared';

import { ApiError, createApiClient, type ApiClient } from '../../lib/api';

export interface Session {
	api: ApiClient;
	/** Exchanges Telegram initData for an access token. Concurrent calls share one request. */
	login(): Promise<TelegramAuthResponse>;
	/**
	 * Drops the token and refuses further logins until the app is reopened. Used after account
	 * deletion (T-804): a silent re-login would restore the account straight away.
	 */
	end(): void;
}

/**
 * Silent auth (ARCHITECTURE §4). The access token lives only in this closure: never in
 * localStorage/sessionStorage/cookies. On 401 the API client calls `refresh`, which repeats login.
 */
export function createSession(options: {
	initData: string | null;
	baseUrl: string;
	fetch?: typeof fetch;
}): Session {
	let token: string | null = null;
	let inflight: Promise<TelegramAuthResponse> | null = null;
	let ended = false;

	const api = createApiClient({
		baseUrl: options.baseUrl,
		...(options.fetch === undefined ? {} : { fetch: options.fetch }),
		auth: {
			getToken: () => token,
			refresh: async () => (await login()).accessToken,
		},
	});

	function login(): Promise<TelegramAuthResponse> {
		const { initData } = options;
		if (ended) return Promise.reject(new ApiError('unauthorized', 401, 'Session ended'));
		if (initData === null) {
			return Promise.reject(new ApiError('unauthorized', 401, 'Not running inside Telegram'));
		}
		inflight ??= api
			.call(routes.telegramAuth, { body: { initData } })
			.then((response) => {
				token = response.accessToken;
				return response;
			})
			.finally(() => {
				inflight = null;
			});
		return inflight;
	}

	return {
		api,
		login,
		end() {
			ended = true;
			token = null;
		},
	};
}
